import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ClientConfigService } from 'client-config-service-lib';
import { BullhornDataSourceApiService, BullhornEntityTypes } from 'datasource-service-lib';
import { Namespace, SectionType, Workflow } from 'enums-lib';
import { INamedEntity } from 'interfaces-lib';
import { IMultiselectListItem } from 'multi-select-autocomplete-lib';
import { forkJoin, map } from 'rxjs';
import { IdentityStorage } from 'storage-service-lib';
import { ServiceProviderServiceFactory } from '../../../../services/service-providers/service-provider-service.factory';
import { SectionFormFactory } from '../../../sections/section-form-factory';
import { IDocMergeAllowedEntitiesService } from '../../allowedEntities/IDocMergeAllowedEntitiesService';
import { AllowedSectionTypes } from '../../allowedSections/AllowedSectionTypes';
import { IDocMergeAllowedSectionsService } from '../../allowedSections/IDocMergeAllowedSectionsService';
import { WorkflowConfigComponent } from '../workflow-config.component';

@Component({
  selector: 'app-docmerge-workflow-config',
  templateUrl: './docmerge-workflow-config.component.html',
  styleUrls: ['./docmerge-workflow-config.component.css', '../workflow-config.component.scss'],
  standalone: false,
})
export class DocmergeWorkflowConfigComponent implements OnInit {
  @Input() parent: WorkflowConfigComponent;
  @Input() allowedSectionTypes: AllowedSectionTypes;
  @Output() delete = new EventEmitter<void>();
  @Output() copyDirectLink = new EventEmitter<void>();
  @Output() discardLocalChanges = new EventEmitter<any>();
  @Output() updateDocumentForDestinationType = new EventEmitter<any>();
  @Output() onDocxFileNameChange = new EventEmitter<any>();
  @Output() saveWorkflowConfig = new EventEmitter<void>();

  allowedEntityService: IDocMergeAllowedEntitiesService;
  allowedSectionService: IDocMergeAllowedSectionsService;
  expandedSectionIndex: number | null = null;

  isDocMergeEnabled = false;
  docMergeEnabled$ = this.clientConfigService.isDocMergeEnabled();

  allCertifications: IMultiselectListItem[] | undefined = undefined;
  allCertificationGroups: IMultiselectListItem[] | undefined = undefined;

  protected readonly Workflow = Workflow;

  constructor(
    private serviceProviderFactory: ServiceProviderServiceFactory,
    private identityStorage: IdentityStorage,
    private datasourceService: BullhornDataSourceApiService,
    private clientConfigService: ClientConfigService
  ) {
    const namespace = this.identityStorage.get().namespace;
    this.allowedEntityService = this.serviceProviderFactory.getServiceProviderService(namespace).getDocMergeAllowedEntitiesService();
    this.allowedSectionService = this.serviceProviderFactory.getServiceProviderService(namespace).getDocMergeAllowedSectionsService();
  }

  get combinedOutput(): boolean {
    return !!this.parent.signatureGroup.get('properties.combinedOutput')?.value;
  }

  get showActionsTab() {
    const isEntitySelected = !!this.parent.entity;
    const isBullhorn = this.parent.namespace === Namespace.bullhorn; // KAD2-2683 - only when Docusign is enabled
    const isSalesforce = this.parent.namespace === Namespace.salesforce;

    return isEntitySelected && ((this.isDocMergeEnabled && isBullhorn) || isSalesforce);
  }

  ngOnInit(): void {
    const namespace = this.identityStorage.get().namespace;
    this.allowedSectionTypes =
      this.allowedSectionService?.getAllowedSections(this.parent.configuration.get('entity').value) ?? this.allowedSectionTypes;

    this.parent.subscriptions.add(
      this.parent.configuration.get('entity').valueChanges.subscribe((entityValue) => {
        this.allowedSectionTypes = this.allowedSectionService?.getAllowedSections(entityValue) ?? this.allowedSectionTypes;
        this.parent.clearDynamicSections();
      }),
    );

    this.parent.subscriptions.add(
      this.docMergeEnabled$.subscribe((enabled) => {
        this.isDocMergeEnabled = enabled;
      }));
    if (namespace === Namespace.bullhorn) {
      forkJoin({
        certs: this.datasourceService.query2(BullhornEntityTypes.Certification, { query: 'id > 0', fields: 'id,name' }),
        groups: this.datasourceService.query2(BullhornEntityTypes.CertificationGroup, { query: 'id > 0', fields: 'id,name' }),
      })
        .pipe(
          map(({ certs, groups }) => ({
            certs: (certs as INamedEntity[]).map((c) => ({ id: Number(c.id), label: c.name })),
            groups: (groups as INamedEntity[]).map((c) => ({ id: Number(c.id), label: c.name })),
          })),
        )
        .subscribe(({ certs, groups }) => {
          this.allCertifications = certs;
          this.allCertificationGroups = groups;
        });
    }
  }

  addSection(sectionType: SectionType): void {
    switch (sectionType) {
      case SectionType.File:
        return this.addFileSection();
      case SectionType.FixedFile:
        return this.addFixedFileSection();
      case SectionType.DocMergeTemplateSection:
        return this.addTemplateSection();
      case SectionType.Certification:
        return this.addCertificationSection();
      default:
        throw new Error('Unknown section type: ' + sectionType);
    }
  }

  addFileSection(): void {
    const fileSection = SectionFormFactory.build(SectionType.File);
    fileSection.get('displayName')?.setValue('New File Section');
    fileSection.get('displayName')?.setValidators([Validators.required]);
    fileSection.get('referenceId')?.setValue(this.nextRefId(SectionType.File));
    fileSection.get('config.attachmentUploadSource')?.setValue(this.identityStorage.get().namespace);
    this.parent.addDynamicSection(fileSection);

    const newIndex = this.parent.getDynamicSections().length - 1;
    this.setExpandedSection(newIndex);
  }

  addFixedFileSection(): void {
    const fixedFile = SectionFormFactory.build(SectionType.FixedFile);
    fixedFile.get('displayName')?.setValue('New Fixed File Section');
    fixedFile.get('displayName')?.setValidators([Validators.required]);
    fixedFile.get('referenceId')?.setValue(this.nextRefId(SectionType.FixedFile));
    this.parent.addDynamicSection(fixedFile);

    const newIndex = this.parent.getDynamicSections().length - 1;
    this.setExpandedSection(newIndex);
  }

  addTemplateSection(): void {
    const templateSection = SectionFormFactory.build(SectionType.DocMergeTemplateSection);
    templateSection.get('displayName')?.setValue('New Template Section');
    templateSection.get('displayName')?.setValidators([Validators.required]);
    templateSection.get('referenceId')?.setValue(this.nextRefId(SectionType.DocMergeTemplateSection));
    this.parent.addDynamicSection(templateSection);

    const newIndex = this.parent.getDynamicSections().length - 1;
    this.setExpandedSection(newIndex);
  }

  addCertificationSection(): void {
    const certificationSection = SectionFormFactory.build(SectionType.Certification);
    certificationSection.get('displayName')?.setValue('New Certification Section');
    certificationSection.get('displayName')?.setValidators([Validators.required]);
    certificationSection.get('referenceId')?.setValue(this.nextRefId(SectionType.Certification));
    this.parent.addDynamicSection(certificationSection);

    const newIndex = this.parent.getDynamicSections().length - 1;
    this.setExpandedSection(newIndex);
  }

  setExpandedSection(index: number): void {
    setTimeout(() => {
      this.expandedSectionIndex = index;
    }, 0);
  }

  reorderSections(event: { previousIndex: number; currentIndex: number }): void {
    const actualPreviousIndex = this.parent.getDynamicSectionIndex(event.previousIndex);
    const actualCurrentIndex = this.parent.getDynamicSectionIndex(event.currentIndex);
    this.parent.reorderSections(actualPreviousIndex, actualCurrentIndex);
  }

  private nextRefId(sectionType: SectionType) {
    const sections = this.parent.getDynamicSections().filter((section: FormGroup) => section.get('type')?.value === sectionType);

    if (sections.length < 1) {
      return sectionType + '_1';
    }

    const sectionIds = sections.map((section: FormGroup) => {
      const fullId = section.get('referenceId').value;
      return parseInt(fullId.split('_').pop(), 10);
    });
    const sorted = sectionIds.sort((a: number, b: number) => a - b);
    const lastRefId = sorted.pop();
    return sectionType + '_' + (Number(lastRefId) + 1);
  }
}
